import React, { useState, useRef, useEffect, useMemo } from "react";
import PhoneInput, { isValidPhoneNumber, getCountries, getCountryCallingCode } from "react-phone-number-input";
import ru from 'react-phone-number-input/locale/ru';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import CHint from "../../UI/CHint";
import { LANG_EN, LANG_RU } from "../../../constants/common";

const InternationalIcon = () => {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="international-icon">
            <path d="M10 0C8.0222 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761211 6.17317C0.00433075 8.0004 -0.193699 10.0111 0.192151 11.9509C0.578001 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.9889 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.6868 19.7413 7.38642 19.2388 6.17317C18.7363 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.7612C12.6136 0.25866 11.3132 0 10 0ZM17.4959 6H14.3286C14.0485 4.54918 13.4846 3.16797 12.6692 1.9357C14.738 2.62447 16.465 4.07863 17.4959 6ZM18.5 10C18.5003 10.8475 18.3734 11.6902 18.1236 12.5H14.5865C14.6945 11.671 14.7491 10.836 14.75 10C14.7491 9.164 14.6945 8.329 14.5865 7.5H18.1236C18.3734 8.3098 18.5003 9.1525 18.5 10ZM10 18.5C8.7773 18.5 7.7156 16.6771 7.1769 14H12.8231C12.2844 16.6771 11.2227 18.5 10 18.5ZM6.9412 12.5C6.75294 10.8386 6.75294 9.1614 6.9412 7.5H13.0588C13.1537 8.3299 13.2008 9.1646 13.2 10C13.2008 10.8354 13.1537 11.6701 13.0588 12.5H6.9412ZM1.5 10C1.49968 9.1525 1.62656 8.3098 1.8764 7.5H5.4135C5.1955 9.1595 5.1955 10.8405 5.4135 12.5H1.8764C1.62656 11.6902 1.49968 10.8475 1.5 10ZM10 1.5C11.2227 1.5 12.2844 3.3228 12.8231 6H7.1769C7.7156 3.3228 8.7773 1.5 10 1.5ZM7.3308 1.9357C6.51543 3.16797 5.9515 4.54918 5.6714 6H2.5041C3.53504 4.07863 5.26197 2.62447 7.3308 1.9357ZM2.5041 14H5.6714C5.95148 15.4508 6.51541 16.832 7.3308 18.0643C5.26199 17.3755 3.53506 15.9213 2.5041 14ZM12.6692 18.0643C13.4846 16.832 14.0485 15.4508 14.3286 14H17.4959C16.4649 15.9213 14.738 17.3755 12.6692 18.0643Z" fill="white" />
        </svg>
    );
};

const countries = getCountries();

const CustomCountrySelect = ({
    $locale,
    value,
    iconComponent: Icon,
    labelsRu,
    labelsEn,
    onChange,
}) => {
    const [selectedCountry, setSelectedCountry] = useState('');
    const { name: langName, localeName } = $locale;

    useEffect(() => {
        !!value ? setSelectedCountry(value) : setSelectedCountry('');
    }, [value]);

    const handleChangeCountry = (value) => {
        onChange(value);
        setSelectedCountry(value);
    };

    const sortedCountries = useMemo(() => {
        if (langName === LANG_RU || localeName === LANG_RU) {
            countries.sort((a, b) => {
                return labelsRu[a].localeCompare(labelsRu[b], LANG_RU);
            });
        } else {
            countries.sort((a, b) => {
                return labelsEn[a].localeCompare(labelsEn[b], LANG_EN);
            });
        }

        return countries;
    }, [langName, localeName]);

    return (
        <>
            <Icon
                aria-hidden
                label={selectedCountry}
                country={selectedCountry}
            />
            <select
                value={value}
                className="ui-input-phone-select"
                onChange={(e) => handleChangeCountry(e.target.value)}
            >
                {sortedCountries.map((country) => (
                    <option
                        key={country}
                        value={country}
                    >
                        {labelsRu[country]} ({labelsEn[country]}) +{getCountryCallingCode(country)}
                    </option>
                ))}
            </select>
        </>
    );
};

const UIPhoneNumber = ({
    $locale,
    name,
    labelClassName,
    label,
    required,
    value,
    onChange,
    error,
    errorName,
    placeholder,
    hint,
    ...props
}) => {

    const [maxLength, setMaxLength] = useState('');
    const inputRef = useRef(null);

    const handleChangeValue = (inputValue) => {
        if (!inputValue) {
            setMaxLength('');
        } else if (isValidPhoneNumber(inputValue?.toString())) {
            setMaxLength(inputRef.current.value.length);
        }

        const inputError = isValidPhoneNumber(inputValue?.toString() || "") ? null : 'auth.login.wrongPhoneFormat';
        onChange(inputValue, inputError);
    };

    return (
        <div className="ui-input-component ui-input-phone">
            {!!label && (
                <div className="ui-input-label">
                    <label
                        htmlFor={name}
                        className={labelClassName}
                    >
                        {label}
                    </label>
                    {hint && <CHint text={hint} />}
                </div>
            )}
            {error && errorName &&
                <label className="input-error">{errorName}</label>
            }
            {error && typeof error !== 'boolean' &&
                <label className="input-error">{error}</label>
            }
            <div className={`ui-input-phone-container ${error ? 'input-error' : ''}`}>
                <svg className="select-button" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 16 16" fill="none">
                    <path d="M7.1343 9.83325C7.5192 10.4999 8.48145 10.4999 8.86635 9.83325L10.0211 7.83325C10.406 7.16659 9.92483 6.33325 9.15503 6.33325H6.84563C6.07583 6.33325 5.5947 7.16658 5.9796 7.83325L7.1343 9.83325Z" fill="white" />
                </svg>
                <PhoneInput
                    ref={inputRef}
                    name={name}
                    value={value?.toString()}
                    placeholder={placeholder}
                    required={required}
                    maxLength={maxLength}
                    international
                    internationalIcon={InternationalIcon}
                    defaultCountry="RU"
                    countrySelectComponent={CustomCountrySelect}
                    countrySelectProps={{
                        $locale: $locale,
                        labelsRu: ru,
                        labelsEn: en,
                        disabled: props?.disabled || false,
                    }}
                    onChange={handleChangeValue}
                    {...props}
                />
            </div>
        </div>
    );
};

export default UIPhoneNumber;
